import { defineElement } from '@umbraco-ui/uui-base/lib/registration';
import { LitElement, html, css } from 'lit';
import { query, property } from 'lit/decorators.js';
import { UUIEvent } from '@umbraco-ui/uui-base/lib/events';
import { LabelMixin } from '@umbraco-ui/uui-base/lib/mixins';
import { demandCustomElement } from '@umbraco-ui/uui-base/lib/utils';

var __defProp$1 = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
class UUIFileDropzoneEvent extends UUIEvent {
  constructor(evName, eventInit = {}) {
    super(evName, __spreadValues(__spreadValues({}, { bubbles: true }), eventInit));
  }
}
UUIFileDropzoneEvent.CHANGE = "change";

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let UUIFileDropzoneElement = class extends LabelMixin("", LitElement) {
  constructor() {
    super();
    this._acceptedFileExtensions = [];
    this._acceptedMimeTypes = [];
    this.multiple = false;
    this.addEventListener("dragenter", this._onDragEnter, false);
    this.addEventListener("dragleave", this._onDragLeave, false);
    this.addEventListener("dragover", this._onDragOver, false);
    this.addEventListener("drop", this._onDrop, false);
  }
  set accept(value) {
    if (value) {
      const mimetypes = [];
      const fileextensions = [];
      value.split(",").forEach((item) => {
        item = item.trim().toLowerCase();
        if (new RegExp("[a-z]+\\/[a-z*]", "s").test(item)) {
          mimetypes.push(item);
        } else {
          fileextensions.push(item.replace(/^\./, ""));
        }
      });
      this._acceptedMimeTypes = mimetypes;
      this._acceptedFileExtensions = fileextensions;
    } else {
      this._acceptedMimeTypes = [];
      this._acceptedFileExtensions = [];
    }
  }
  /**
   * Opens the native file picker to select a file.
   * @method browse
   */
  browse() {
    this._input.click();
  }
  connectedCallback() {
    super.connectedCallback();
    demandCustomElement(this, "uui-symbol-file-dropzone");
  }
  async _getAllFileEntries(dataTransferItemList) {
    const fileEntries = [];
    const queue = [...dataTransferItemList];
    while (queue.length > 0) {
      const entry = queue.shift();
      if (entry.kind === "file") {
        const file = entry.getAsFile();
        if (!file)
          continue;
        if (this._isAccepted(file)) {
          fileEntries.push(file);
        }
      } else if (entry.kind === "directory") {
        if ("webkitGetAsEntry" in entry === false)
          continue;
        const directory = entry.webkitGetAsEntry();
        queue.push(
          ...await this._readAllDirectoryEntries(directory.createReader())
        );
      }
    }
    return fileEntries;
  }
  // Get all the entries (files or sub-directories) in a directory
  // by calling readEntries until it returns empty array
  async _readAllDirectoryEntries(directoryReader) {
    const entries = [];
    let readEntries = await this._readEntriesPromise(directoryReader);
    while (readEntries.length > 0) {
      entries.push(...readEntries);
      readEntries = await this._readEntriesPromise(directoryReader);
    }
    return entries;
  }
  async _readEntriesPromise(directoryReader) {
    return new Promise((resolve, reject) => {
      try {
        directoryReader.readEntries(resolve, reject);
      } catch (err) {
        console.log(err);
        reject(err);
      }
    });
  }
  _isAccepted(file) {
    if (this._acceptedFileExtensions.length === 0 && this._acceptedMimeTypes.length === 0) {
      return true;
    }
    const fileType = file.type.toLowerCase();
    const fileExtension = file.name.split(".").pop();
    if (fileExtension && this._acceptedFileExtensions.includes(fileExtension.toLowerCase())) {
      return true;
    }
    for (const mimeType in this._acceptedMimeTypes) {
      if (fileType === mimeType) {
        return true;
      } else if (mimeType.endsWith("/*") && fileType.startsWith(mimeType.replace("/*", ""))) {
        return true;
      }
    }
    return false;
  }
  async _onDrop(e) {
    var _a;
    e.preventDefault();
    this._dropzone.classList.remove("hover");
    const items = (_a = e.dataTransfer) == null ? void 0 : _a.items;
    if (items) {
      let result = await this._getAllFileEntries(items);
      if (this.multiple === false && result.length) {
        result = [result[0]];
      }
      this.dispatchEvent(
        new UUIFileDropzoneEvent(UUIFileDropzoneEvent.CHANGE, {
          detail: { files: result }
        })
      );
    }
  }
  _onDragOver(e) {
    e.preventDefault();
  }
  _onDragEnter(e) {
    this._dropzone.classList.add("hover");
    e.preventDefault();
  }
  _onDragLeave(e) {
    this._dropzone.classList.remove("hover");
    e.preventDefault();
  }
  _onFileInputChange() {
    const files = this._input.files ? Array.from(this._input.files) : [];
    this.dispatchEvent(
      new UUIFileDropzoneEvent(UUIFileDropzoneEvent.CHANGE, {
        detail: { files }
      })
    );
  }
  render() {
    return html`
      <div id="dropzone">
        <uui-symbol-file-dropzone id="symbol"></uui-symbol-file-dropzone>
        ${this.renderLabel()}
        <input
          @click=${(e) => e.stopImmediatePropagation()}
          id="input"
          type="file"
          accept=${this.accept}
          ?multiple=${this.multiple}
          @change=${this._onFileInputChange}
          aria-label="${this.label}" />
        <slot></slot>
      </div>
    `;
  }
};
UUIFileDropzoneElement.styles = [
  css`
      #dropzone {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        position: relative;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        padding: var(--uui-size-4,12px);
        border: 3px solid transparent;
        margin: -3px;
        backdrop-filter: blur(2px);
      }
      #dropzone.hover {
        border-color: var(--uui-color-default,#1b264f);
      }
      #dropzone.hover::before {
        content: '';
        position: absolute;
        inset: 0;
        opacity: 0.2;
        border-color: var(--uui-color-default,#1b264f);
        background-color: var(--uui-color-default,#1b264f);
      }
      #symbol {
        color: var(--uui-color-default,#1b264f);
        max-width: 100%;
        max-height: 100%;
      }
      #input {
        position: absolute;
        width: 0px;
        height: 0px;
        opacity: 0;
        display: none;
      }
    `
];
__decorateClass([
  query("#input")
], UUIFileDropzoneElement.prototype, "_input", 2);
__decorateClass([
  query("#dropzone")
], UUIFileDropzoneElement.prototype, "_dropzone", 2);
__decorateClass([
  property({ type: String })
], UUIFileDropzoneElement.prototype, "accept", 1);
__decorateClass([
  property({ type: Boolean })
], UUIFileDropzoneElement.prototype, "multiple", 2);
UUIFileDropzoneElement = __decorateClass([
  defineElement("uui-file-dropzone")
], UUIFileDropzoneElement);

export { UUIFileDropzoneElement, UUIFileDropzoneEvent };
